import { FC, useMemo, useState } from 'react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import {
  InlineNotification,
  Modal,
  Search,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { ArrowRight } from '@carbon/react/icons';
import { CompareSection } from 'components/compare-section';
import { FormattedCount } from 'components/formatted-count';
import { Date as FormattedDate } from 'components/date';
import type { WellWorkspaceDetail } from 'types/well-workspace';
import { useCompare } from '../hooks';
import { tableSections, type CompareTableSection } from './compare-utils';
import styles from './compare-sheet.module.scss';

interface Props {
  open: boolean;
  workspace: WellWorkspaceDetail;
  /** The well the page is scoped to, called out in the list. */
  selectedUwi: string | null;
  onClose: () => void;
}

interface CellTag {
  label: string;
  type: 'blue' | 'green' | 'cool-gray';
}

/**
 * The Action column: what the row does to the record. The backend states it; a new
 * record is a create and everything else — a change, a replacement, a removal — edits
 * one that exists.
 * @param {'create' | 'edit'} action The backend's action for the row.
 * @return {CellTag} The label and Carbon tag colour to draw.
 */
const actionTag = (action: 'create' | 'edit'): CellTag => {
  return action === 'create'
    ? { label: 'Create', type: 'green' }
    : { label: 'Edit', type: 'blue' };
};

/**
 * The Promoted column: whether the change has reached the wells. The backend states it
 * per row; when it does not, the workspace's own state stands in.
 * @param {'promoted' | 'staged' | null} state The backend's state for the row.
 * @param {boolean} workspacePromoted Whether the workspace has been promoted.
 * @return {{ lifecycle: 'promoted' } | CellTag} Promoted, which LifecycleStatusTag draws, or
 *   the label and Carbon tag colour to draw.
 */
const promotedTag = (
  state: 'promoted' | 'staged' | null,
  workspacePromoted: boolean
): { lifecycle: 'promoted' } | CellTag => {
  const resolved = state ?? (workspacePromoted ? 'promoted' : 'staged');

  return resolved === 'promoted'
    ? { lifecycle: 'promoted' }
    : { label: 'Staged', type: 'cool-gray' };
};

/**
 * One dataset section: a collapsible header carrying its change count, over the mock's
 * Well / Field / Promoted / Staged / Status table.
 * @param {object} props The section, whether the workspace is promoted, and the scoped well.
 * @return {JSX.Element} The section.
 */
const DatasetSection: FC<{
  section: CompareTableSection;
  promoted: boolean;
  selectedUwi: string | null;
}> = ({ section, promoted, selectedUwi }) => {
  return (
    <CompareSection label={section.label} count={section.rows.length}>
      <Table size="lg" className={styles.table}>
        <TableHead>
          <TableRow>
            <TableHeader>Well</TableHeader>
            <TableHeader>Field</TableHeader>
            <TableHeader>Promoted (previous)</TableHeader>
            <TableHeader>Staged (new)</TableHeader>
            <TableHeader>Action</TableHeader>
            <TableHeader>Promoted</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {section.rows.map((row) => {
            const action = actionTag(row.action);
            const state = promotedTag(row.promoted, promoted);

            return (
              <TableRow key={row.id}>
                <TableCell>
                  <span className={styles.wellCell}>
                    {row.well}
                    {row.uwi === selectedUwi && (
                      <Tag type="blue" size="sm">
                        On screen
                      </Tag>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {row.label}
                  {row.period && (
                    <span className={styles.rowPeriod}>
                      {' '}
                      <FormattedDate date={row.period} />
                    </span>
                  )}
                </TableCell>
                <TableCell className={styles.from}>{row.from}</TableCell>
                <TableCell>
                  <span className={styles.to}>
                    <ArrowRight className={styles.arrow} aria-hidden />
                    {row.to}
                  </span>
                </TableCell>
                <TableCell>
                  <Tag type={action.type} size="sm">
                    {action.label}
                  </Tag>
                </TableCell>
                <TableCell>
                  {'lifecycle' in state ? (
                    <LifecycleStatusTag status={state.lifecycle} />
                  ) : (
                    <Tag type={state.type} size="sm">
                      {state.label}
                    </Tag>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CompareSection>
  );
};

/**
 * Compare to Promoted — what this workspace changes against the canonical record.
 *
 * ORIGIN is what is promoted to the database, TARGET is this workspace. Rows group by
 * the card they appear on and the scoped well is tagged. A promoted workspace reads a
 * frozen diff, so the sheet stays truthful after the canonical record moves.
 * @param {Props} props The workspace, the scoped well, and the close handler.
 * @return {JSX.Element} The tearsheet.
 */
export const CompareSheet: FC<Props> = ({
  open,
  workspace,
  selectedUwi,
  onClose,
}) => {
  const [search, setSearch] = useState('');

  // Served by the backend already grouped and rolled up, carrying only changed wells
  // and fields, so nothing here filters blank-to-blank rows. No client-side fallback: a
  // second grouping in the frontend is what used to drift from the server's.
  const served = useCompare(workspace.id, open);
  const sections = useMemo(
    () => tableSections(served.data?.sections ?? [], search),
    [served.data, search]
  );

  // The backend counts these, so the summary line cannot disagree with the rows. Its
  // `edits` already covers the Related Documents rows (one per addition, removal and
  // replacement), so nothing is added on top and no document is listed twice.
  const rollup = served.data?.rollup;
  const changedWells = rollup?.wellsChanged ?? 0;
  const totalWells = rollup?.totalWells ?? workspace.wells.length;
  const totalChanges = rollup?.edits ?? 0;

  return (
    <Modal
      open={open}
      className={styles.sheet}
      modalLabel="Well Workspace"
      modalHeading="Compare to Promoted"
      primaryButtonText="Close"
      size="lg"
      onRequestClose={onClose}
      onRequestSubmit={onClose}
    >
      <div className={styles.sides}>
        <div className={styles.side}>
          <span className={styles.sideBadge}>ORIGIN</span>
          <span className={styles.sideLabel}>Promoted to database</span>
        </div>
        <div className={`${styles.side} ${styles.sideTarget}`}>
          <span className={`${styles.sideBadge} ${styles.sideBadgeTarget}`}>
            TARGET
          </span>
          <span className={styles.sideLabel}>
            {workspace.name}
            {/* The mock keeps "staged edits" after promotion and marks the state in
                brackets, rather than swapping the noun for a date — what the side
                holds is the same set either way. */}
            <span className={styles.asOf}>
              {' '}
              · staged edits{workspace.promoted ? ' (promoted)' : ''}
            </span>
          </span>
        </div>
      </div>

      {/* "staged edits" whether or not the workspace is promoted, as the mock has it;
          promotion adds a tail rather than renaming what was staged. Only once the
          diff has arrived: before that the counts would read as "0 changes", which is
          the one thing this sheet must never say by accident. */}
      {served.data && !served.isError && (
        <p className={styles.summary}>
          <FormattedCount value={changedWells} /> of{' '}
          <FormattedCount value={totalWells} /> wells changed ·{' '}
          <FormattedCount value={totalChanges} /> staged edit
          {totalChanges === 1 ? '' : 's'}
          {workspace.promoted ? ' · promoted to the wells' : ''}
        </p>
      )}

      {workspace.wells.length > 1 && (
        <div className={styles.search}>
          <Search
            size="sm"
            labelText="Search wells"
            placeholder="Search wells by name or UWI"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
          />
        </div>
      )}

      {/* Loading and failure come before the empty state on purpose: with neither
          branch, an in-flight or failed request read as "no changes", which is the
          one thing a compare sheet must never say by accident. */}
      {served.isPending ? (
        <SkeletonText paragraph lineCount={4} />
      ) : served.isError ? (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle="Could not load this workspace's changes."
          lowContrast
          hideCloseButton
        />
      ) : totalChanges === 0 ? (
        <p className={styles.empty}>
          No staged edits in this workspace yet — it matches what is promoted.
        </p>
      ) : (
        <>
          {/* Only a search can be answered with "nothing matched". Every change is a
              row in some section — related documents included — so an empty list with
              no search means the rollup and the rows disagree, and blaming a search
              nobody made (a one-well workspace has no search box) would be wrong. */}
          {sections.length === 0 ? (
            search.trim() ? (
              <p className={styles.empty}>No wells match that search.</p>
            ) : null
          ) : (
            sections.map((section) => (
              <DatasetSection
                key={section.key}
                section={section}
                promoted={workspace.promoted}
                selectedUwi={selectedUwi}
              />
            ))
          )}
        </>
      )}
    </Modal>
  );
};
